/* eslint-disable react/prop-types,jsx-a11y/anchor-is-valid */
import React from 'react';
import {
  withStateHandlers,
  setDisplayName,
  compose,
  withHandlers
} from 'recompose';
import config from '../../config/config';
import UserInput from './Common/UserInput';
import LogoSmall from '../../static/images/header_logo_small.svg';
import { validEmail, validPhoneNumber } from '../stores/auth';
import { track } from '../helpers/Tracking/Analytics';
import Captcha from './Common/Captcha';
import Loader from './UI/Loader';
import AppleSignInButton from './Common/AppleSignInButton';
import GlobalErrorModal from './Common/GlobalErrorModal';
import useFeature from '../hooks/useFeature';

import '../../static/scss/components/login.scss';

export const DefaultCreateAccountForm = ({
  register,
  setEmail,
  setPassword,
  setFirstName,
  setLastName,
  registerError,
  loggingIn,
  toggleTerms,
  togglePrivacy,
  showErrors,
  registration,
  verifyCaptcha,
  setRecaptcha,
  getRecaptcha,
  executeCaptcha
}) => {
  const cypress = 'login-create';

  return (
    <div className="c-login c-login--create">
      <form>
        <img src={LogoSmall} alt="" />
        <h5>Create an account to continue</h5>
        <div className="row collapse">
          <div className="small-12 large-4 column">First name</div>
          <div className="small-12 large-8 column">
            <UserInput
              type="text"
              placeholder="First name"
              value={registration.firstName}
              onChange={setFirstName}
              testId="registrationFirstName"
            />
          </div>
        </div>

        <div className="row collapse">
          <div className="small-12 large-4 column">Surname</div>
          <div className="small-12 large-8 column">
            <UserInput
              type="text"
              placeholder="Surname"
              value={registration.lastName}
              onChange={setLastName}
              testId="registrationLastName"
            />
          </div>
        </div>

        <div className="row collapse">
          <div className="small-12 large-4 column">Email address</div>
          <div className="small-12 large-8 column">
            <UserInput
              type="email"
              placeholder="example@mail.com"
              value={registration.email}
              onChange={setEmail}
              testId="registrationEmail"
            />
          </div>
        </div>

        <div className="row collapse ">
          <div className="small-12 large-4 column">Password</div>
          <div className="small-12 large-8 column large-offset-4 c-login__create-password">
            <UserInput
              type="password"
              placeholder="Password"
              errorMessage="Password too weak"
              pattern=".{8,}"
              value={registration.password}
              onChange={setPassword}
              testId="registrationPassword"
            />
            <p>Please choose a secure password (at least 8 characters)</p>
            <p>
              By proceeding with creating an account you agree to the JustPark{' '}
              <a
                href="#"
                className="terms-btn terms-btn--terms"
                onClick={toggleTerms}
              >
                Terms & Conditions
              </a>{' '}
              and{' '}
              <a
                href="#"
                className="terms-btn terms-btn--privacy"
                onClick={togglePrivacy}
              >
                Privacy Policy.
              </a>
            </p>
          </div>
        </div>

        <div className="row collapse">
          <p className="c-login__error">
            {registration.showingErrors
              ? registerError ||
                registration.nameError ||
                registration.emailError ||
                registration.passwordError
              : ''}
          </p>
        </div>

        <div className="c-login__submit">
          <div>
            <a href="#" onClick={register}>
              Cancel
            </a>
          </div>
          <div>
            <div className="loading-wrapper">
              <div className={loggingIn ? 'is-loading' : ''}>
                <input
                  className={registration.registerValid ? 'valid' : ''}
                  data-cy={`${cypress}--submit`}
                  onClick={e => {
                    e.preventDefault();
                    if (!loggingIn && registration.registerValid) {
                      getRecaptcha().reset();
                      showErrors();
                      executeCaptcha();
                    } else {
                      showErrors();
                    }
                  }}
                  type="submit"
                  value="Create account"
                />
                <Captcha
                  size="invisible"
                  onChange={verifyCaptcha}
                  ref={setRecaptcha}
                  badge="bottomleft"
                />
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export const createAccountFormComposer = compose(
  withStateHandlers(
    {
      firstName: '',
      lastName: '',
      password: '',
      email: '',
      nameError: '',
      passwordError: '',
      emailError: '',
      showingErrors: false,
      registerValid: false
    },
    {
      showErrors: (state, props) => () => {
        const stateUpdate = {
          showingErrors: true
        };
        props.updateRegForm(stateUpdate);
        return stateUpdate;
      },
      setFirstName: (state, props) => firstName => {
        props.updateRegForm({
          firstName
        });
        return state;
      },
      setLastName: (state, props) => lastName => {
        props.updateRegForm({
          lastName
        });
        return state;
      },
      setPassword: (state, props) => password => {
        props.updateRegForm({
          password
        });
        return state;
      },
      setEmail: (state, props) => email => {
        props.updateRegForm({
          email
        });
        return state;
      }
    }
  ),
  setDisplayName('CreateAccountForm'),
  withHandlers(() => {
    let recaptcha;

    return {
      setRecaptcha: () => ref => {
        recaptcha = ref;
      },
      getRecaptcha: () => () => recaptcha,
      verifyCaptcha: props => token => {
        if (!token) {
          recaptcha.execute();
          return;
        }

        props.completeRegister(
          props.queryParams,
          props.registration.email,
          props.registration.firstName,
          props.registration.lastName,
          props.registration.password,
          token
        );
      },
      executeCaptcha: props => () => {
        if (config.isTesting) {
          props.completeRegister(
            props.queryParams,
            props.registration.email,
            props.registration.firstName,
            props.registration.lastName,
            props.registration.password,
            ''
          );
          return;
        }

        if (typeof recaptcha.execute !== 'function') {
          throw new Error('Captcha ref was not set in time to execute');
        }

        recaptcha.execute();
      }
    };
  })
);

export const CreateAccountForm = createAccountFormComposer(
  DefaultCreateAccountForm
);

export const ConfusedForm = ({ back }) => (
  <div className="c-login">
    <form>
      <a href={`${__CLIENT__ ? window.location.origin : ''}`}>
        <img src={LogoSmall} alt="" />
      </a>
      <h5>Help resetting your password</h5>
      <div className="content">
        We recommend entering your email address on this screen. You should only
        enter your phone number if you used the JustPark pay by phone service
        before using the JustPark app or website.
      </div>
      <div className="row collapse submit">
        <div className="small-6 large-6 column">
          <a href="#" onClick={back}>
            Back
          </a>
        </div>
      </div>
    </form>
  </div>
);

const validResetPassword = ({ emailError }) => !emailError;

const getResetPasswordErrors = ({ email }) => ({
  emailError: validPhoneNumber(email).length === 0 ? '' : validEmail(email)
});

export const PasswordResetForm = withStateHandlers(
  {
    email: ''
  },
  {
    updateEmail: state => email => ({
      ...getResetPasswordErrors({ ...state, email }),
      email,
      passwordResetValid: validResetPassword(
        getResetPasswordErrors({ ...state, email })
      )
    })
  }
)(
  setDisplayName('PasswordResetForm')(
    ({
      forgot,
      updateEmail,
      email,
      resetPassword,
      loggingIn,
      toggleConfused,
      passwordResetValid,
      resetPasswordMessage,
      resetPasswordError
    }) => (
      <div className="c-login">
        <form>
          <a href={__CLIENT__ ? window.location.origin : ''}>
            <img src={LogoSmall} alt="" />
          </a>
          <h5>Enter email or phone number to reset your password</h5>
          <div
            className={`row collapse ${
              resetPasswordError || resetPasswordMessage ? '' : 'offset-bottom'
            }`}
          >
            <div className="small-12 large-4 column">Email or phone number</div>
            <div className="small-12 large-8 column">
              <UserInput
                value={email}
                onChange={updateEmail}
                type="email"
                placeholder="example@mail.com"
                testId="resetPasswordEmail"
              />
            </div>
          </div>
          <div className="row collapse">
            <div className="c-login__right large-offset-4">
              <p className="right">
                <a href="#" onClick={toggleConfused}>
                  I&apos;m confused
                </a>
              </p>
            </div>
          </div>
          {(resetPasswordMessage || resetPasswordError) && (
            <div className="row collapse offset-bottom">
              <div className="small-12 large-8 column large-offset-4">
                {resetPasswordMessage || resetPasswordError}
              </div>
            </div>
          )}
          <div className="c-login__submit">
            <div>
              <a href="#" onClick={forgot}>
                {resetPasswordMessage || resetPasswordError ? 'Back' : 'Cancel'}
              </a>
            </div>
            <div>
              <div className="loading-wrapper">
                <div className={loggingIn ? 'is-loading' : ''}>
                  <input
                    className={passwordResetValid ? 'valid' : ''}
                    onClick={e => {
                      e.preventDefault();
                      resetPassword(email);
                    }}
                    type="submit"
                    data-test-id="loginSubmit"
                    data-cy="loginSubmit"
                    value="Reset password"
                  />
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    )
  )
);

const validLogin = ({ passwordError, emailError }) =>
  !passwordError && !emailError;

const getLoginErrors = ({ password, email }) => ({
  emailError: validPhoneNumber(email).length === 0 ? '' : validEmail(email),
  passwordError: password.length > 0 ? '' : 'You must enter a password'
});

const LoginFormTemplate = ({
  email,
  password,
  updateEmail,
  updatePassword,
  login,
  forgot,
  loginError,
  loggingIn,
  emailError,
  passwordError,
  loginValid,
  showErrors,
  showingErrors,
  setRecaptcha,
  getRecaptcha,
  verifyCaptcha,
  executeCaptcha
}) => (
  <div className="c-login">
    <form>
      <a href={__CLIENT__ ? window.location.origin : ''}>
        <img src={LogoSmall} alt="" />
      </a>
      <h5>Welcome back! Let’s get you signed in.</h5>
      <div className="row collapse">
        <div className="small-12 large-4 column">Email address</div>
        <div className="small-12 large-8 column">
          <UserInput
            value={email}
            onChange={updateEmail}
            type="email"
            placeholder="example@mail.com"
            testId="loginEmail"
          />
        </div>
      </div>
      <div className="row collapse">
        <div className="small-12 large-4 column">Password</div>
        <div className="small-12 large-8 column">
          <UserInput
            value={password}
            onChange={updatePassword}
            type="password"
            placeholder="password"
            testId="loginPassword"
          />
        </div>
      </div>
      <div className="row collapse">
        <div className="c-login__right c-login__forgotten-password">
          <p className="right">
            <a href="#" onClick={forgot}>
              I&lsquo;ve forgotten my password
            </a>
          </p>
        </div>
      </div>
      <div className="c-login__error">
        <p>{showingErrors ? loginError || emailError || passwordError : ''}</p>
      </div>
      <div className="c-login__submit">
        <div>
          <a href="" onClick={login}>
            Cancel
          </a>
        </div>
        <div>
          <div className="loading-wrapper">
            <div className={loggingIn ? 'is-loading' : ''}>
              <input
                className={loginValid ? 'valid' : ''}
                onClick={e => {
                  e.preventDefault();
                  if (!loggingIn && loginValid) {
                    // reset before submission because onChange in Recaptcha doesn't always fire
                    getRecaptcha().reset();
                    showErrors();
                    executeCaptcha();
                  } else {
                    showErrors();
                  }
                }}
                type="submit"
                value="Sign in"
              />
              <Captcha
                size="invisible"
                onChange={verifyCaptcha}
                ref={setRecaptcha}
                badge="bottomleft"
              />
            </div>
          </div>
        </div>
      </div>
    </form>
  </div>
);

// all the state needed for the login form to work
export const loginFormComposer = compose(
  withStateHandlers(
    {
      email: '',
      password: '',
      emailError: '',
      passwordError: '',
      showingErrors: false
    },
    {
      showErrors: state => () => ({
        ...getLoginErrors(state),
        showingErrors: true
      }),
      updateEmail: state => email => ({
        ...getLoginErrors({ ...state, email }),
        email,
        loginValid: validLogin(getLoginErrors({ ...state, email }))
      }),
      updatePassword: state => password => ({
        ...getLoginErrors({ ...state, password }),
        password,
        loginValid: validLogin(getLoginErrors({ ...state, password }))
      }),
      setToken: () => token => ({
        token
      })
    }
  ),
  setDisplayName('LoginForm'),
  withHandlers(() => {
    let recaptcha;

    return {
      setRecaptcha: () => ref => {
        recaptcha = ref;
      },
      getRecaptcha: () => () => recaptcha,
      verifyCaptcha: props => token => {
        if (!token) {
          recaptcha.execute();
          return;
        }

        props.setToken(token);

        props.completeLogin(
          props.queryParams,
          props.email,
          props.password,
          token
        );
      },
      executeCaptcha: props => () => {
        if (config.isTesting) {
          props.completeLogin(
            props.queryParams,
            props.registration.email,
            props.registration.firstName,
            props.registration.lastName,
            props.registration.password,
            ''
          );
          return;
        }

        if (typeof recaptcha.execute !== 'function') {
          throw new Error('Captcha ref was not set in time to execute');
        }

        recaptcha.execute();
      }
    };
  })
);

export const LoginForm = loginFormComposer(LoginFormTemplate);
const LoginWallForm = ({
  fbLogin,
  googLogin,
  register,
  login,
  facebookLoading,
  googleLoading,
  toggleTerms,
  togglePrivacy,
  queryParams,
  apiError,
  clearApiError
}) => {
  const cypress = 'login-wall';
  const isAppleSignInDisabled = useFeature('disable_jp_web_apple_sign_in');

  return (
    <>
      <div className="c-login">
        <form>
          <div className="c-login__wall row collapse">
            <div className="small-12 large-5 column vertical-space-between hero">
              <a href={__CLIENT__ ? window.location.origin : ''}>
                <img src={LogoSmall} alt="" />
              </a>
              <h5>You need to sign in or create an account to continue.</h5>
            </div>
            <div className="small-12 large-6 large-offset-1 column">
              {!isAppleSignInDisabled ? (
                <div className="row collapse">
                  <AppleSignInButton />
                </div>
              ) : null}

              <div className="row collapse">
                <button
                  type="button"
                  className={`bt bt--fb ${
                    facebookLoading ? 'bt--loading' : ''
                  }`}
                  disabled={facebookLoading}
                  onClick={e => {
                    e.preventDefault();
                    track('click:login-wall:facebook');
                    fbLogin(queryParams);
                  }}
                >
                  <>
                    {facebookLoading ? <Loader /> : null}
                    <span className="bt--fb__text">
                      Continue with <strong>Facebook</strong>
                    </span>
                  </>
                </button>
              </div>
              <div className="row collapse">
                <button
                  type="button"
                  className={`bt bt--google ${
                    googleLoading ? 'bt--loading' : ''
                  }`}
                  disabled={googleLoading}
                  onClick={e => {
                    e.preventDefault();
                    track('click:login-wall:google');
                    googLogin(queryParams);
                  }}
                >
                  <>
                    {googleLoading ? <Loader /> : null}
                    <span span className="bt--google__text">
                      Continue with <strong>Google</strong>
                    </span>
                  </>
                </button>
              </div>
              <div className="row collapse">
                <a href="#" onClick={login} className="bt--secondary">
                  Sign in with my email address
                </a>
              </div>
              <div className="row collapse">
                <a
                  href="#"
                  onClick={register}
                  className="bt--secondary"
                  data-cy={`${cypress}--create-account`}
                >
                  Create account with my email address
                </a>
              </div>
            </div>
          </div>
          <div className="row collapse footer">
            <p className="terms-footer">
              By continuing with this purchasing process you indicate that you
              have read and agree with our{' '}
              {
                <a href="#" onClick={togglePrivacy}>
                  Privacy Policy
                </a>
              }{' '}
              and{' '}
              {
                <a href="#" onClick={toggleTerms}>
                  Terms & Conditions.
                </a>
              }
            </p>
          </div>
        </form>
      </div>
      <GlobalErrorModal
        errorTitle={apiError?.title}
        errorCode={apiError?.code}
        errorInfo={apiError?.info}
        acknowledgeErrorAction={clearApiError}
        noOverflow
      />
    </>
  );
};

const Login = props => <LoginWallForm {...props} />;

export default Login;
